import { useMemo, useCallback, useState, type FC } from 'react';
import { Table as TableComponent } from '@lucid/lego';
import {
  downloadSfdcFileFromFilehub,
  fetchSfdcFileBlob,
} from '../../../../../../api/services/lupp/fileUpload/fileUpload';
import { createColumns, createHistoryData } from './utils';
import type { ModuleFileReference } from './utils';

type TableProps = {
  theme: 'light' | 'dark';
  invoices: ModuleFileReference[];
  sfdcDeliveryId: string;
  labels: Record<string, string>;
  dataTestId?: string;
  compact?: boolean;
};

/**
 * Pro-Forma History Table component
 * @component
 * @param props - Table component props
 * @returns Pro-Forma history table
 */
const Table: FC<TableProps> = ({
  theme,
  invoices,
  sfdcDeliveryId,
  labels,
  dataTestId = 'orderhub-proforma-history-table',
  compact = false,
}) => {
  const invertedTheme = theme === 'light' ? 'dark' : 'light';
  const noProFormaHistoryLabel =
    labels?.['proforma.noHistory'] || 'No Pro-Forma Invoices available';

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadClick = useCallback(
    async (sfdcFileId: string, fileName: string) => {
      setDownloadingId(sfdcFileId);
      try {
        await downloadSfdcFileFromFilehub(sfdcDeliveryId, sfdcFileId, fileName);
      } catch (error) {
        console.error('Failed to download pro-forma invoice:', error);
      } finally {
        setDownloadingId(null);
      }
    },
    [sfdcDeliveryId]
  );

  const handleFileNameClick = useCallback(
    async (sfdcFileId: string, _fileName: string) => {
      try {
        const { blob } = await fetchSfdcFileBlob(sfdcDeliveryId, sfdcFileId);
        // Ensure the blob has the correct PDF MIME type for proper browser rendering
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          console.error('Failed to open new tab - popup may be blocked');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (error) {
        console.error('Failed to open pro-forma invoice:', error);
      }
    },
    [sfdcDeliveryId]
  );

  const columns = useMemo(
    () =>
      createColumns({
        theme,
        labels,
        onDownloadClick: handleDownloadClick,
        isDownloading: !!downloadingId,
        downloadingDocumentId: downloadingId,
        onFileNameClick: handleFileNameClick,
      }),
    [theme, labels, handleDownloadClick, downloadingId, handleFileNameClick]
  );

  const historyData = useMemo(() => createHistoryData({ invoices, labels }), [invoices, labels]);

  return (
    <TableComponent
      columns={columns}
      data={historyData}
      theme={invertedTheme}
      compact={compact}
      selection={{ enabled: false }}
      emptyState={{ message: noProFormaHistoryLabel }}
      gaComponent="orderhub-proforma-history-table"
      dataTestId={dataTestId}
    />
  );
};

export default Table;
